import type { RiskRecognitionHighlight } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';
import { useTranslation } from '@cobliteam/i18n';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import RiskFootageThumbnail from './RiskFootageThumbnail';

interface RiskFootagePreviewProps {
  highlight: RiskRecognitionHighlight;
  /** Clique (abre o modal de footage). Sem `onClick` é só um thumbnail estático. */
  onClick?: () => void;
  size?: 'sm' | 'md';
  className?: string;
}

const SIZE_CLASS: Record<NonNullable<RiskFootagePreviewProps['size']>, string> = {
  sm: 'h-11 w-16',
  md: 'h-16 w-24',
};

/**
 * Thumbnail de footage do highlight. Existe quando há `metadata.media_ids` (o vídeo real é resolvido sob
 * demanda em `/media` ao clicar — ver RiskFootageDialog). Usa `signed_media_urls[0]` como poster quando o
 * backend manda; senão cai num placeholder de vídeo. Overlay de play + badge de contagem (>1 clipe).
 * Retorna `null` sem media_ids, então pode ser montado incondicionalmente.
 */
const RiskFootagePreview = ({ highlight, onClick, size = 'md', className }: RiskFootagePreviewProps) => {
  const { t } = useTranslation('notifications');
  const mediaIds = highlight.metadata?.media_ids ?? [];
  if (mediaIds.length === 0) return null;

  const interactive = Boolean(onClick);
  const label = t('feed.footage_count', { count: mediaIds.length });

  return (
    <button
      type='button'
      onClick={onClick}
      disabled={!interactive}
      aria-label={label}
      title={label}
      className={cn(
        'group relative shrink-0 overflow-hidden rounded-lg border border-gray-5 bg-black',
        SIZE_CLASS[size],
        interactive ? 'cursor-pointer' : 'cursor-default',
        className
      )}
    >
      <RiskFootageThumbnail mediaId={mediaIds[0]} poster={highlight.metadata?.signed_media_urls?.[0]} />
      <span className='absolute inset-0 flex items-center justify-center bg-black/25 transition-colors group-hover:bg-black/10'>
        <Icon name='play' size='sm' className='text-white drop-shadow' aria-hidden />
      </span>
      {mediaIds.length > 1 ? (
        <span className='pointer-events-none absolute right-1 bottom-1 inline-flex items-center gap-0.5 rounded bg-black/60 px-1 py-0.5'>
          <Icon name='video' size='xs' className='text-white' aria-hidden />
          <Typography as='span' variant='caption-xs' className='text-white tabular-nums leading-none'>
            {mediaIds.length}
          </Typography>
        </span>
      ) : null}
    </button>
  );
};

export default RiskFootagePreview;
